import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { RATIO } from 'mm-theme-configuration/dist/consts';
import { useTheme } from '../../../theming/useTheme';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { CardComponentDataProps } from '../../partials/cards/cards.utils';
import {
  componentConfigThemeKey as onTopCardConfigThemeKey,
  OnTopCard,
  OnTopCardComponentThemeProps,
} from '../../partials/cards/onTopCard/OnTopCard';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import { baseUnit } from '../../../theming/baseUnitDefinition';
import { useThemeOverride } from '../../../theming/useThemeOverride';
import { ThemeContext } from '../../../theming/ThemeProviderWithFonts';
import {
  createImageHeightCalculationMethods,
  createImageWidths,
  ImageHeightCalculationMethods,
} from '../../partials/image/image.utils';
import { LayoutComponentBaseProps, getLayoutComponentPadding } from '../pageLayouts/layout.utils';

export const componentConfigThemeKey = 'twoOneTwoArticleList';

export interface TwoOneTwoArticleListThemeProps {
  largeVerticalGap: number;
  largeHorizontalGap: number;
  mediumVerticalGap: number;
  mediumHorizontalGap: number;
  smallHorizontalGap: number;
  minorCardsRatioLarge: RATIO;
  minorCardsRatioMedium: RATIO;
}

interface TwoOneTwoArticleListDataComponent extends LayoutComponentBaseProps {
  cards: Array<CardComponentDataProps>;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createStyle = ({ largeHorizontalGap, largeVerticalGap, mediumHorizontalGap, mediumVerticalGap, smallHorizontalGap, minorCardsRatioLarge, minorCardsRatioMedium }: TwoOneTwoArticleListThemeProps) => StyleSheet.create({
  wrapper: {
    display: 'grid',
    [MEDIA_BREAKPOINTS.large]: {
      gridTemplateColumns: '1fr 2fr 1fr',
      gridTemplateRows: `${minorCardsRatioLarge.x}fr ${minorCardsRatioLarge.y}fr`,
      gridColumnGap: `${largeVerticalGap * baseUnit}px`,
      gridRowGap: `${largeHorizontalGap * baseUnit}px`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      gridTemplateColumns: `${minorCardsRatioMedium.x}fr ${minorCardsRatioMedium.y}fr`,
      gridTemplateRows: 'auto auto auto',
      gridColumnGap: `${mediumVerticalGap * baseUnit}px`,
      gridRowGap: `${mediumHorizontalGap * baseUnit}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      gridTemplateColumns: '1fr',
      gridRowGap: `${smallHorizontalGap * baseUnit}px`,
    },
  },
  secondCard: {
    [MEDIA_BREAKPOINTS.large]: {
      gridRowStart: '2',
      gridRowEnd: '3',
      gridColumnStart: '1',
    },
    [MEDIA_BREAKPOINTS.small]: {
      gridRowStart: 'unset',
      gridRowEnd: 'unset',
      gridColumnStart: 'unset',
    },
  },
  mainCard: {
    [MEDIA_BREAKPOINTS.large]: {
      gridRowStart: '1',
      gridRowEnd: '3',
      gridColumnStart: '2',
    },
    [MEDIA_BREAKPOINTS.medium]: {
      gridRowStart: '2',
      gridRowEnd: '3',
      gridColumnStart: '1',
      gridColumnEnd: '3',
    },
    [MEDIA_BREAKPOINTS.small]: {
      order: '-1',
      gridRowStart: 'unset',
      gridRowEnd: 'unset',
      gridColumnStart: 'unset',
      gridColumnEnd: 'unset',
    },
  },
  fourthCard: {
    [MEDIA_BREAKPOINTS.medium]: {
      gridRowStart: '3',
      gridRowEnd: '4',
      gridColumnStart: '1',
      gridColumnEnd: '2',
    },
    [MEDIA_BREAKPOINTS.small]: {
      gridRowStart: 'unset',
      gridRowEnd: 'unset',
      gridColumnStart: 'unset',
      gridColumnEnd: 'unset',
    },
  },
  fifthCard: {
    [MEDIA_BREAKPOINTS.large]: {
      gridColumnStart: '3',
    },
    [MEDIA_BREAKPOINTS.medium]: {
      gridRowStart: '3',
      gridRowEnd: '4',
      gridColumnStart: '2',
      gridColumnEnd: '3',
    },
    [MEDIA_BREAKPOINTS.small]: {
      gridRowStart: 'unset',
      gridRowEnd: 'unset',
      gridColumnStart: 'unset',
      gridColumnEnd: 'unset',
    },
  },
});

const addImageHeightCalculationMethodsProp = (cardProps: CardComponentDataProps, imageHeightCalculationMethods: ImageHeightCalculationMethods) => ({
  ...cardProps,
  imageHeightCalculationMethods,
});

const imageWidthsMainCard = createImageWidths(360, 720, 540);
const imageWidthsMinorCard = createImageWidths(360, 360, 360);

export const TwoOneTwoArticleList: React.FunctionComponent<TwoOneTwoArticleListDataComponent> = props => {
  const { cards, paddingFactors } = props;
  const themeProps = useTheme<TwoOneTwoArticleListThemeProps>(componentConfigThemeKey);
  const firstCard = addImageHeightCalculationMethodsProp(cards[1], createImageHeightCalculationMethods('ASPECT_RATIO', 'ASPECT_RATIO', '100%_HEIGHT'));
  const secondCard = addImageHeightCalculationMethodsProp(cards[2], createImageHeightCalculationMethods('ASPECT_RATIO', '100%_HEIGHT', '100%_HEIGHT'));
  const mainCard = addImageHeightCalculationMethodsProp(cards[0], createImageHeightCalculationMethods('ASPECT_RATIO', 'ASPECT_RATIO', 'ASPECT_RATIO'));
  const fourthCard = addImageHeightCalculationMethodsProp(cards[3], createImageHeightCalculationMethods('ASPECT_RATIO', 'ASPECT_RATIO', '100%_HEIGHT'));
  const fifthCard = addImageHeightCalculationMethodsProp(cards[4], createImageHeightCalculationMethods('ASPECT_RATIO', '100%_HEIGHT', '100%_HEIGHT'));
  const style = createStyle(themeProps);
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const themeContextMainCard = useThemeOverride<OnTopCardComponentThemeProps>({
    titleFontSizeMedium: 'large',
    titleFontSizeLarge: 'large',
    titleFontSizeSmall: 'bigger',
    horizontalGapSmall: 0.25,
    horizontalGapMedium: 0.5,
    horizontalGapLarge: 0.5,
    aspectRatioSmall: { x: 4, y: 3 },
  }, onTopCardConfigThemeKey);
  const themeContextMinorCards = useThemeOverride<OnTopCardComponentThemeProps>({
    titleFontSizeMedium: 'bigger',
    titleFontSizeLarge: 'bigger',
    titleFontSizeSmall: 'bigger',
    horizontalGapSmall: 0.25,
    horizontalGapMedium: 0.25,
    horizontalGapLarge: 0.25,
  }, onTopCardConfigThemeKey);
  return (
    <ErrorBoundary>
      {cards.length > 0
      && (
      <div className={css(paddingStyle, style.wrapper)}>
        <ThemeContext.Provider value={themeContextMinorCards}>
          <OnTopCard {...firstCard} imageWidths={imageWidthsMinorCard} />
          <div className={css(style.secondCard)}>
            <OnTopCard {...secondCard} imageWidths={imageWidthsMinorCard} />
          </div>
        </ThemeContext.Provider>
        <ThemeContext.Provider value={themeContextMainCard}>
          <div className={css(style.mainCard)}>
            <OnTopCard {...mainCard} imageWidths={imageWidthsMainCard} />
          </div>
        </ThemeContext.Provider>
        <ThemeContext.Provider value={themeContextMinorCards}>
          <div className={css(style.fourthCard)}>
            <OnTopCard {...fourthCard} imageWidths={imageWidthsMinorCard} />
          </div>
          <div className={css(style.fifthCard)}>
            <OnTopCard {...fifthCard} imageWidths={imageWidthsMinorCard} />
          </div>
        </ThemeContext.Provider>
      </div>
      )}
    </ErrorBoundary>
  );
};
